/**
 * Variant comparison table.
 *
 * - Collapses/expands row groups.
 * - Highlights the column of the currently selected variant and keeps it in sync
 *   with the product form via the theme's 'on:variant:change' event.
 * - "Select" buttons drive the main variant picker.
 * - Toggles edge shadows and a hint while the table can scroll sideways.
 */
if (!customElements.get('variant-comparison')) {
  class VariantComparison extends HTMLElement {
    constructor() {
      super();
      this.scroller = this.querySelector('.mkg-vc__scroller');
      this.labelColumn = this.querySelector('.mkg-vc__corner');

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundUpdateScrollState = this.updateScrollState.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.boundHandleClick);
      document.addEventListener('on:variant:change', this.boundHandleVariantChange);

      if (this.scroller) {
        this.scroller.addEventListener('scroll', this.boundUpdateScrollState, { passive: true });
        window.addEventListener('on:debounced-resize', this.boundUpdateScrollState);
      }

      this.setActiveColumn(this.dataset.selectedVariant, false);
      this.updateScrollState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundHandleClick);
      document.removeEventListener('on:variant:change', this.boundHandleVariantChange);

      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this.boundUpdateScrollState);
        window.removeEventListener('on:debounced-resize', this.boundUpdateScrollState);
      }
    }

    /**
     * Routes clicks on group toggles and "Select" buttons.
     * @param {MouseEvent} evt
     */
    handleClick(evt) {
      const toggle = evt.target.closest('.mkg-vc__group-toggle');
      if (toggle) {
        VariantComparison.toggleGroup(toggle);
        return;
      }

      const selectBtn = evt.target.closest('.mkg-vc__select');
      if (selectBtn) {
        evt.preventDefault();
        this.selectVariant(selectBtn);
      }
    }

    /**
     * Expands or collapses a row group.
     * @param {HTMLButtonElement} toggle
     */
    static toggleGroup(toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      toggle.closest('.mkg-vc__group')?.classList.toggle('is-collapsed', expanded);
    }

    /**
     * Follows the product form's selected variant. Ignores pickers inside quick-buy
     * drawers or featured product sections, which belong to other products.
     * @param {CustomEvent} evt - 'on:variant:change' event.
     */
    handleVariantChange(evt) {
      if (evt.target.closest('.js-quickbuy, .cc-featured-product')) return;
      const variant = evt.detail?.variant;
      this.setActiveColumn(variant ? variant.id : null, true);
    }

    /**
     * Marks every cell of the given variant's column as active and scrolls it into view.
     * @param {string|number|null} variantId
     * @param {boolean} smooth - Animate the horizontal scroll.
     */
    setActiveColumn(variantId, smooth) {
      const id = variantId ? String(variantId) : '';
      this.querySelectorAll('[data-variant-id]').forEach((cell) => {
        cell.classList.toggle('is-active', cell.dataset.variantId === id);
      });

      if (id) this.scrollColumnIntoView(id, smooth);
    }

    /**
     * Scrolls the table sideways so the given column is fully visible, accounting
     * for the sticky label column.
     * @param {string} variantId
     * @param {boolean} smooth
     */
    scrollColumnIntoView(variantId, smooth) {
      if (!this.scroller) return;
      const header = this.querySelector(`thead [data-variant-id="${CSS.escape(variantId)}"]`);
      if (!header) return;

      const scrollerRect = this.scroller.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      const labelWidth = this.labelColumn ? this.labelColumn.getBoundingClientRect().width : 0;
      const visibleLeft = scrollerRect.left + labelWidth;

      let delta = 0;
      if (headerRect.left < visibleLeft) {
        delta = headerRect.left - visibleLeft;
      } else if (headerRect.right > scrollerRect.right) {
        delta = Math.min(headerRect.right - scrollerRect.right, headerRect.left - visibleLeft);
      }

      if (delta !== 0) {
        this.scroller.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' });
      }
    }

    /**
     * Selects a variant in the main product form by choosing each of its option values,
     * mirroring how the variant picker itself selects options. Falls back to loading the
     * variant URL if the picker can't be found.
     * @param {HTMLButtonElement} btn
     */
    selectVariant(btn) {
      const picker = document.querySelector('.cc-main-product variant-picker')
        || Array.from(document.querySelectorAll('variant-picker'))
          .find((el) => !el.closest('.js-quickbuy, .cc-featured-product'));

      let options = [];
      try {
        options = JSON.parse(btn.dataset.options || '[]');
      } catch (e) {
        options = [];
      }

      if (!picker || options.length === 0) {
        if (btn.dataset.url) window.location.href = btn.dataset.url;
        return;
      }

      picker.querySelectorAll('.option-selector').forEach((selector, index) => {
        const value = options[index];
        if (value === undefined) return;

        const option = Array.from(selector.querySelectorAll('.js-option')).find((el) => {
          const optionValue = el.dataset.value !== undefined ? el.dataset.value : el.value;
          return optionValue === value;
        });
        if (!option || option.matches(':checked, [aria-selected="true"]')) return;

        const customSelect = option.closest('custom-select');
        if (customSelect && typeof customSelect.selectOption === 'function') {
          customSelect.selectOption(option);
        } else {
          option.click();
        }
      });

      this.setActiveColumn(btn.dataset.variantId, true);
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /**
     * Toggles the edge shadows and mobile hint depending on how far the table can scroll.
     */
    updateScrollState() {
      if (!this.scroller) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      const maxScroll = scrollWidth - clientWidth;
      const scrollable = maxScroll > 1;

      this.classList.toggle('is-scrollable', scrollable);
      this.classList.toggle('is-scrollable-left', scrollable && scrollLeft > 1);
      this.classList.toggle('is-scrollable-right', scrollable && scrollLeft < maxScroll - 1);
    }
  }

  customElements.define('variant-comparison', VariantComparison);
}
